package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlin.js.Date
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.PerehdytysMateriaali
import com.kaihuoy.shared.data.PerehdytysPohja
import com.kaihuoy.shared.data.PerehdytysVastaus
import com.kaihuoy.shared.data.PohjaKysymys
import com.kaihuoy.shared.data.Tyomaa
import com.kaihuoy.shared.data.User
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import kotlinx.browser.window

@JsName("encodeURIComponent")
private external fun encodeURIComponent(value: String): String

internal val KOHDERYHMAT = listOf(
    "kaikki" to "Kaikki käyttäjät",
    "huolto" to "Huolto + Admin",
    "admin"  to "Vain admin"
)

internal fun buildPdfUrl(rawUrl: String): String =
    "/pdf-viewer.html?v=3#$rawUrl"

// =====================================================
// MAIN SCREEN
// =====================================================

@Composable
fun InductionScreen(user: User?, isAdmin: Boolean, onLogout: () -> Unit) {
    var activeTab by remember { mutableStateOf("Materiaalit") }

    var materiaalit by remember { mutableStateOf<List<PerehdytysMateriaali>>(emptyList()) }
    var isMateriaaliLoading by remember { mutableStateOf(true) }
    var showAddForm by remember { mutableStateOf(false) }

    var pohjat by remember { mutableStateOf<List<PerehdytysPohja>>(emptyList()) }
    var isPohjatLoading by remember { mutableStateOf(true) }
    var showCreatePohja by remember { mutableStateOf(false) }
    var selectedPohja by remember { mutableStateOf<PerehdytysPohja?>(null) }
    var editingPohja by remember { mutableStateOf<PerehdytysPohja?>(null) }

    val scope = rememberCoroutineScope()

    var luetutIds by remember { mutableStateOf<Set<String>>(emptySet()) }


    LaunchedEffect(user?.uid) {
        if (user == null) { isMateriaaliLoading = false; isPohjatLoading = false; return@LaunchedEffect }
        val canManage = isAdmin || user.isHuolto
        try {
            launch {
                FirebaseService.database.reference("perehdytys_materiaalit").valueEvents.collect { s ->
                    val all = s.children.mapNotNull { child ->
                        try { val m = child.value<PerehdytysMateriaali>(); if (m.id.isBlank()) m.copy(id = child.key ?: "") else m }
                        catch (e: Exception) { null }
                    }.sortedByDescending { it.lisatty }
                    materiaalit = all.filter { when (it.kohderyhma) { "kaikki" -> true; "huolto" -> canManage; "admin" -> isAdmin; else -> true } }
                    isMateriaaliLoading = false
                }
            }
            launch {
                FirebaseService.database.reference("perehdytys_pohjat").valueEvents.collect { s ->
                    val all = s.children.mapNotNull { child ->
                        try { val p = child.value<PerehdytysPohja>(); if (p.id.isBlank()) p.copy(id = child.key ?: "") else p }
                        catch (e: Exception) { null }
                    }.sortedByDescending { it.lisatty }
                    pohjat = all.filter { when (it.kohderyhma) { "kaikki" -> true; "huolto" -> canManage; "admin" -> isAdmin; else -> true } }
                    isPohjatLoading = false
                }
            }
            launch {
                FirebaseService.database.reference("materiaalit_luettu").child(user.uid).valueEvents.collect { s ->
                    luetutIds = s.children.mapNotNull { it.key }.toSet()
                }
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
        } catch (e: Exception) { isMateriaaliLoading = false; isPohjatLoading = false }
    }

    when {
        showAddForm && isAdmin -> AddPerehdytysForm(
            user = user!!,
            onSave = { mat ->
                scope.launch {
                    try {
                        val key = FirebaseService.database.reference("perehdytys_materiaalit").push().key ?: return@launch
                        FirebaseService.database.reference("perehdytys_materiaalit").child(key).setValue(mat.copy(id = key))
                        com.kaihuoy.components.common.AppFeedback.showSuccess("Materiaali lisätty")
                        showAddForm = false
                    } catch (e: Exception) { com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}") }
                }
            },
            onUploadFile = { jsFile, fileName, onDone ->
                scope.launch {
                    try {
                        val ref = FirebaseService.storage.reference("perehdytys_materiaalit/${Date.now().toLong()}_$fileName")
                        @Suppress("UNCHECKED_CAST")
                        ref.putData(jsFile.asDynamic().unsafeCast<dev.gitlive.firebase.storage.Data>())
                        onDone(true, ref.getDownloadUrl(), null)
                    } catch (e: Exception) { onDone(false, null, e.message) }
                }
            },
            onCancel = { showAddForm = false }
        )

(showCreatePohja || editingPohja != null) && isAdmin -> CreatePohjaForm(
            user = user!!,
            editingPohja = editingPohja,
            onSave = { pohja ->
                scope.launch {
                    try {
                        val existing = editingPohja
                        if (existing != null) {
                            FirebaseService.database.reference("perehdytys_pohjat").child(existing.id)
                                .setValue(pohja.copy(id = existing.id, lisatty = existing.lisatty, muokattu = Date.now().toLong()))
                            com.kaihuoy.components.common.AppFeedback.showSuccess("Pohja päivitetty")
                        } else {
                            val key = FirebaseService.database.reference("perehdytys_pohjat").push().key ?: return@launch
                            FirebaseService.database.reference("perehdytys_pohjat").child(key).setValue(pohja.copy(id = key))
                            com.kaihuoy.components.common.AppFeedback.showSuccess("Pohja luotu")
                        }
                        showCreatePohja = false
                        editingPohja = null
                    } catch (e: Exception) { com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}") }
                }
            },
            onCancel = { showCreatePohja = false; editingPohja = null }
        )

        selectedPohja != null -> FillPohjaView(
            pohja = selectedPohja!!,
            user = user,
            onBack = { selectedPohja = null }
        )

        else -> PageLayout(greeting = getGreeting(user?.displayName), onLogout = onLogout) {
            Div(attrs = { style { padding(28.px, 32.px); maxWidth(960.px); property("margin", "0 auto") } }) {
            H1(attrs = { style { margin(0.px, 0.px, 16.px, 0.px); color(KaihuTheme.TextPrimary); fontSize(1.8.em); fontWeight("800") } }) {
                Text("Perehdytys")
            }
            }

            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(4.px); marginBottom(20.px)
                    property("background", KaihuTheme.CardGradient); borderRadius(12.px); padding(4.px)
                    property("border", "1px solid ${KaihuTheme.Border}")
                    property("width", "fit-content")
                    property("margin", "0 auto")
                }
            }) {
                InductionTab("Materiaalit", materiaalit.size, "Materiaalit" == activeTab) { activeTab = "Materiaalit" }
                InductionTab("Perehdytys", pohjat.size, "Perehdytys" == activeTab) { activeTab = "Perehdytys" }
            }

            when (activeTab) {
                "Materiaalit" -> MaterialitContent(
                    materiaalit = materiaalit,
                    isLoading = isMateriaaliLoading,
                    isAdmin = isAdmin,
                    luetutIds = luetutIds,
                    onAddClick = { showAddForm = true },
                    onOpen = { mat ->
                        val uid = user?.uid
                        if (uid != null && mat.id !in luetutIds) {
                            scope.launch {
                                try {
                                    FirebaseService.database.reference("materiaalit_luettu").child(uid).child(mat.id).setValue(kotlin.js.Date.now().toLong())
                                } catch (_: Exception) {}
                            }
                        }
                        val viewerUrl = "/pdf-viewer.html?returnTo=${encodeURIComponent("/#/perehdytys")}&v=3#${mat.tiedostoUrl}"
                        window.location.href = viewerUrl
                    },
                    onDelete = { mat ->
                        com.kaihuoy.components.common.AppFeedback.confirm("Poistetaanko materiaali?", "Poistetaanko \"${mat.otsikko}\"?", "Poista", confirmColor = KaihuTheme.Danger) {
                            scope.launch {
                                try {
                                    FirebaseService.database.reference("perehdytys_materiaalit").child(mat.id).removeValue()
                                    if (mat.tiedostoUrl.contains("firebasestorage")) {
                                        try { FirebaseService.storage.reference("perehdytys_materiaalit/${mat.id}").delete() } catch (_: Exception) {}
                                    }
                                    com.kaihuoy.components.common.AppFeedback.showSuccess("Poistettu")
                                } catch (e: Exception) { com.kaihuoy.components.common.AppFeedback.showError("Poisto epäonnistui") }
                            }
                        }
                    }
                )
                else -> PohjatContent(
                    pohjat = pohjat,
                    isLoading = isPohjatLoading,
                    isAdmin = isAdmin,
                    onCreatePohja = { showCreatePohja = true },
                    onSelectPohja = { selectedPohja = it },
                    onEditPohja = { editingPohja = it },
                    onDeletePohja = { pohja ->
                        com.kaihuoy.components.common.AppFeedback.confirm("Poistetaanko pohja?", "Poistetaanko \"${pohja.otsikko}\"?", "Poista", confirmColor = KaihuTheme.Danger) {
                            scope.launch {
                                try {
                                    FirebaseService.database.reference("perehdytys_pohjat").child(pohja.id).removeValue()
                                    com.kaihuoy.components.common.AppFeedback.showSuccess("Pohja poistettu")
                                } catch (e: Exception) { com.kaihuoy.components.common.AppFeedback.showError("Poisto epäonnistui") }
                            }
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun InductionTab(label: String, count: Int, isActive: Boolean, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            flex(1); padding(10.px); borderRadius(10.px); textAlign("center"); cursor("pointer")
            fontWeight(if (isActive) "700" else "500"); fontSize(0.9.em)
            backgroundColor(if (isActive) KaihuTheme.Primary else Color.transparent)
            color(if (isActive) Color.white else KaihuTheme.TextSecondary)
            property("transition", "all 0.15s"); property("user-select", "none")
            maxWidth(150.px)
        }
    }) {
        Text(label)
        if (count > 0) Span(attrs = {
            style {
                marginLeft(6.px)
                backgroundColor(if (isActive) rgba(255, 255, 255, 0.25) else KaihuTheme.Border)
                color(if (isActive) Color.white else KaihuTheme.TextSecondary)
                padding(1.px, 7.px); borderRadius(20.px); fontSize(0.82.em); fontWeight("700")

            }
        }) { Text(count.toString()) }
    }
}

// =====================================================
// MATERIAALIT CONTENT
// =====================================================

@Composable
private fun MaterialitContent(
    materiaalit: List<PerehdytysMateriaali>,
    isLoading: Boolean,
    isAdmin: Boolean,
    luetutIds: Set<String>,
    onAddClick: () -> Unit,
    onOpen: (PerehdytysMateriaali) -> Unit,
    onDelete: (PerehdytysMateriaali) -> Unit
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center); marginBottom(16.px); flexWrap(FlexWrap.Wrap); gap(12.px)
        }
    }) {
        P(attrs = { style { padding(20.px); margin(0.px); color(KaihuTheme.TextSecondary) } }) {
            Text("${materiaalit.size} ${if (materiaalit.size == 1) "materiaali" else "materiaalia"}")
        }
        if (isAdmin) StyledButton("Lisää materiaali", icon = "plus", onClick = { onAddClick() }, backgroundColor = KaihuTheme.Primary)
    }

    when {
        isLoading -> Div(attrs = { style { padding(40.px); textAlign("center"); color(KaihuTheme.TextSecondary) } }) {
            Text("Ladataan materiaaleja...")
        }
        materiaalit.isEmpty() -> Div(attrs = {
            style {
                property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(48.px, 24.px)
                property("border", "1px solid ${KaihuTheme.Border}"); textAlign("center"); color(KaihuTheme.TextSecondary)
            }
        }) {
            Div(attrs = { style { fontSize(2.5.em); marginBottom(12.px) } }) { TIcon("file") }
            Div { Text(if (isAdmin) "Täällä ei ole vielä mitään — lisää ensimmäinen materiaali." else "Ei materiaaleja saatavilla tällä hetkellä.") }
        }
        else -> Div(attrs = {
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fill, minmax(280px, 1fr))")
                gap(16.px)
            }
        }) {
            materiaalit.forEach { mat ->
                MaterialCard(mat, isAdmin, isLuettu = mat.id in luetutIds, onOpen = { onOpen(mat) }, onDelete = { onDelete(mat) })
            }
        }
    }
}

// =====================================================
// MATERIAL CARD
// =====================================================

@Composable
private fun MaterialCard(
    materiaali: PerehdytysMateriaali,
    isAdmin: Boolean,
    isLuettu: Boolean,
    onOpen: () -> Unit,
    onDelete: () -> Unit
) {
    var isHovered by remember { mutableStateOf(false) }
    Div(attrs = {
        style {
            property("background", if (isLuettu) "linear-gradient(135deg, #0d2b1e 0%, #0f3324 100%)" else KaihuTheme.CardGradient)
            borderRadius(16.px); padding(20.px)
            property("border", "1px solid ${if (isLuettu) "#10B98155" else if (isHovered) "#2563EB" else "#E9ECEF"}")
            property("box-shadow", if (isHovered) KaihuTheme.ShadowMd else KaihuTheme.ShadowSm)
            property("transition", "all 0.2s ease")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px)
        }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexStart) } }) {
            Div(attrs = {
                style {
                    width(44.px); height(44.px); backgroundColor(KaihuTheme.PrimaryLight); borderRadius(12.px)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); fontSize(1.4.em)
                }
            }) { TIcon("file") }
            val (bg, fg, lbl) = when (materiaali.kohderyhma) {
                "huolto" -> Triple("#FFF3CD", "#856404", "Huolto+Admin")
                "admin"  -> Triple("#D8F3DC", "#1B4332", "Vain admin")
                else     -> Triple("#334155", "#CBD5E1", "Kaikki")
            }
            Span(attrs = { style { backgroundColor(Color(bg)); color(Color(fg)); padding(3.px, 10.px); borderRadius(20.px); fontSize(0.72.em); fontWeight("700") } }) { Text(lbl) }
        }
        Div {
            Div(attrs = { style { fontWeight("700"); color(KaihuTheme.TextPrimary); marginBottom(4.px) } }) { Text(materiaali.otsikko) }
            if (materiaali.kuvaus.isNotBlank()) Div(attrs = { style { fontSize(0.85.em); color(KaihuTheme.TextSecondary); lineHeight("1.4") } }) { Text(materiaali.kuvaus) }
        }
        if (isLuettu) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
                    backgroundColor(Color("#0d2b1e")); borderRadius(8.px); padding(6.px, 10.px)
                    property("border", "1px solid #10B98144")
                }
            }) {
                Span(attrs = { style { color(Color("#10B981")); fontSize(0.88.em) } }) { TIcon("circle-check") }
                Span(attrs = { style { color(Color("#10B981")); fontSize(0.82.em); fontWeight("600") } }) { Text(" Luettu") }
            }
        }
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); gap(8.px); paddingTop(8.px)
                property("border-top", "1px solid ${KaihuTheme.Border}")
                justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
            }
        }) {
            if (materiaali.tiedostoUrl.isNotBlank()) SmallButton(text = "Avaa PDF", icon = "book", onClick = { onOpen() })
            else Span(attrs = { style { fontSize(0.8.em); color(KaihuTheme.TextLight) } }) { Text("Ei tiedostoa") }
            if (isAdmin) SmallButton(text = "", icon = "trash", onClick = { onDelete() }, isDanger = true)
        }
    }
}

// =====================================================
// ADD MATERIAALIT FORM
// =====================================================

@Composable
private fun AddPerehdytysForm(
    user: User,
    onSave: (PerehdytysMateriaali) -> Unit,
    onUploadFile: (jsFile: Any?, fileName: String, onDone: (Boolean, String?, String?) -> Unit) -> Unit,
    onCancel: () -> Unit
) {
    var otsikko by remember { mutableStateOf("") }
    var kuvaus by remember { mutableStateOf("") }
    var urlInput by remember { mutableStateOf("") }
    var kohderyhma by remember { mutableStateOf("kaikki") }
    var useFileUpload by remember { mutableStateOf(true) }
    var selectedFileName by remember { mutableStateOf("") }
    var selectedFile: Any? by remember { mutableStateOf(null) }
    var isSaving by remember { mutableStateOf(false) }
    var statusMsg by remember { mutableStateOf("") }
    var showStorageBrowser by remember { mutableStateOf(false) }

    Div(attrs = { style { padding(20.px); maxWidth(680.px); property("margin", "0 auto") } }) {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px); marginBottom(24.px) } }) {
            Div(attrs = { onClick { onCancel() }; style { color(KaihuTheme.Primary); cursor("pointer"); fontWeight("600"); fontSize(0.9.em) } }) { Text("← Takaisin") }
            H2(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontWeight("800") } }) { Text("Uusi materiaali") }
        }

        Div(attrs = {
            style {
                property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(24.px)
                property("border", "1px solid ${KaihuTheme.Border}")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(20.px)
            }
        }) {
            StyledInput(label = "Otsikko *", value = otsikko, onValueChange = { otsikko = it }, placeholder = "Esim. Työturvallisuusohjeet 2025")
            StyledInput(label = "Kuvaus (vapaaehtoinen)", value = kuvaus, onValueChange = { kuvaus = it }, placeholder = "Lyhyt kuvaus materiaalista")

            Div {
                Div(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary); marginBottom(10.px); fontSize(0.9.em) } }) { Text("Tiedostolähde") }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); marginBottom(12.px) } }) {
                    SourceToggle("Lataa tiedosto", useFileUpload) { useFileUpload = true }
                    SourceToggle("Syötä URL", !useFileUpload) { useFileUpload = false }
                }
                if (useFileUpload) {
                    Div(attrs = {
                        style {
                            property("border", "2px dashed ${if (selectedFileName.isNotBlank()) "#2563EB" else "#E9ECEF"}")
                            borderRadius(12.px); padding(20.px); textAlign("center")
                            backgroundColor(if (selectedFileName.isNotBlank()) Color("#1E3A5F") else KaihuTheme.Background)
                        }
                    }) {
                        if (selectedFileName.isNotBlank()) Div(attrs = { style { color(KaihuTheme.Primary); fontWeight("600"); marginBottom(8.px) } }) { TIcon("file"); Text(" $selectedFileName") }
                        Label(forId = "pdf-file-input", attrs = {
                            style {
                                display(DisplayStyle.InlineBlock); backgroundColor(KaihuTheme.Primary); color(Color.white)
                                padding(10.px, 20.px); borderRadius(8.px); cursor("pointer"); fontWeight("600"); fontSize(0.88.em)
                            }
                        }) { TIcon("paperclip"); Text(if (selectedFileName.isBlank()) " Valitse PDF-tiedosto" else " Vaihda tiedosto") }
                        Input<String>(type = InputType.File, attrs = {
                            id("pdf-file-input"); attr("accept", ".pdf,application/pdf")
                            style { property("display", "none") }
                            onChange { event ->
                                val files = event.target.asDynamic().files
                                if (files != null && files.length.unsafeCast<Int>() > 0) {
                                    val file = files[0]
                                    selectedFile = file
                                    selectedFileName = file.name.unsafeCast<String>()
                                }
                            }
                        })
                    }
                    Div(attrs = { style { marginTop(8.px) } }) {
                        SmallButton(
                            text = if (showStorageBrowser) "Piilota tiedostoselain" else "Selaa tallennettuja tiedostoja",
                icon = if (showStorageBrowser) null else "cloud",
                            onClick = { showStorageBrowser = !showStorageBrowser }
                        )
                        if (showStorageBrowser) StorageFileBrowser(onSelect = { name, url -> urlInput = url; useFileUpload = false; showStorageBrowser = false; statusMsg = "Valittu: $name" })
                    }
                } else {
                    StyledInput(label = "Tiedoston URL", value = urlInput, onValueChange = { urlInput = it }, placeholder = "https://storage.googleapis.com/...")
                    SmallButton(text = "Selaa tallennettuja tiedostoja", icon = "cloud", onClick = { showStorageBrowser = !showStorageBrowser })
                    if (showStorageBrowser) StorageFileBrowser(onSelect = { _, url -> urlInput = url; showStorageBrowser = false })
                }
            }

            Div {
                Div(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary); marginBottom(10.px); fontSize(0.9.em) } }) { Text("Kohderyhmä") }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
                    KOHDERYHMAT.forEach { (value, label) -> KohderyhmäChip(label, value, kohderyhma == value) { kohderyhma = value } }
                }
            }

            if (statusMsg.isNotBlank()) Div(attrs = { style { fontSize(0.88.em); color(if (statusMsg.startsWith("Virhe")) Color(KaihuTheme.Danger.toString()) else Color("#1D4ED8")); fontWeight("600") } }) { Text(statusMsg) }

            Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); justifyContent(JustifyContent.FlexEnd) } }) {
                StyledButton("Peruuta", onClick = onCancel, backgroundColor = KaihuTheme.Grey)
                StyledButton(
                    text = if (isSaving) "Tallennetaan..." else "Tallenna",
                    enabled = !isSaving,
                    backgroundColor = KaihuTheme.Primary,
                    onClick = {
                        if (otsikko.isBlank()) { com.kaihuoy.components.common.AppFeedback.showWarning("Anna materiaalille otsikko."); return@StyledButton }
                        isSaving = true
                        val mat = PerehdytysMateriaali(otsikko = otsikko.trim(), kuvaus = kuvaus.trim(), tiedostoUrl = urlInput.trim(), lisatty = Date.now().toLong(), lisaajaId = user.uid, kohderyhma = kohderyhma)
                        if (useFileUpload && selectedFile != null) {
                            statusMsg = "Ladataan tiedostoa..."
                            onUploadFile(selectedFile, selectedFileName) { ok, url, err ->
                                if (ok && url != null) { statusMsg = "Tallennetaan..."; onSave(mat.copy(tiedostoUrl = url)) }
                                else { statusMsg = "Virhe: ${err ?: "Tiedoston lataus epäonnistui"}"; isSaving = false }
                            }
                        } else { onSave(mat) }
                    }
                )
            }
        }
    }
}

// =====================================================
// FIREBASE STORAGE BROWSER
// =====================================================

@Composable
private fun StorageFileBrowser(onSelect: (name: String, url: String) -> Unit) {
    var files by remember { mutableStateOf<List<Pair<String, String>>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            val listResult = FirebaseService.storage.reference("perehdytys_materiaalit").listAll()
            files = listResult.items.mapNotNull { ref ->
                try { ref.name to ref.getDownloadUrl() } catch (_: Exception) { null }
            }
        } catch (e: Exception) { error = "Tiedostojen lataus epäonnistui: ${e.message}" }
        isLoading = false
    }

    Div(attrs = {
        style { marginTop(8.px); backgroundColor(KaihuTheme.Background); borderRadius(12.px); padding(12.px); property("border", "1px solid ${KaihuTheme.Border}"); maxHeight(200.px); overflowY("auto") }
    }) {
        when {
            isLoading -> Div(attrs = { style { padding(12.px); textAlign("center"); color(KaihuTheme.TextSecondary); fontSize(0.88.em) } }) { Text("Ladataan...") }
            error != null -> Div(attrs = { style { color(KaihuTheme.Danger); fontSize(0.85.em) } }) { Text(error!!) }
            files.isEmpty() -> Div(attrs = { style { padding(12.px); textAlign("center"); color(KaihuTheme.TextSecondary); fontSize(0.88.em) } }) { Text("Ei tallennettuja tiedostoja") }
            else -> files.forEach { (name, url) ->
                Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); padding(8.px, 4.px); property("border-bottom", "1px solid ${KaihuTheme.Border}"); gap(8.px) } }) {
                    Span(attrs = { style { fontSize(0.85.em); color(KaihuTheme.TextPrimary); flex(1) } }) { TIcon("file"); Text(" ${if (name.length > 40) name.take(37) + "..." else name}") }
                    SmallButton(text = "Valitse", onClick = { onSelect(name, url) })
                }
            }
        }
    }
}

// =====================================================
// POHJAT CONTENT
// =====================================================

@Composable
private fun PohjatContent(
    pohjat: List<PerehdytysPohja>,
    isLoading: Boolean,
    isAdmin: Boolean,
    onCreatePohja: () -> Unit,
    onSelectPohja: (PerehdytysPohja) -> Unit,
    onEditPohja: (PerehdytysPohja) -> Unit,
    onDeletePohja: (PerehdytysPohja) -> Unit
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center); marginBottom(16.px); flexWrap(FlexWrap.Wrap); gap(12.px)
        }
    }) {
        P(attrs = { style { margin(0.px); color(KaihuTheme.TextSecondary) } }) {
            Text("${pohjat.size} pohjaa")
        }
        if (isAdmin) StyledButton("Luo pohja", icon = "plus", onClick = { onCreatePohja() }, backgroundColor = KaihuTheme.Primary)
    }

    when {
        isLoading -> Div(attrs = { style { padding(40.px); textAlign("center"); color(KaihuTheme.TextSecondary) } }) {
            Text("Ladataan...")
        }
        pohjat.isEmpty() -> Div(attrs = {
            style {
                property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(48.px, 24.px)
                property("border", "1px solid ${KaihuTheme.Border}"); textAlign("center"); color(KaihuTheme.TextSecondary)
            }
        }) {
            Div(attrs = { style { fontSize(2.5.em); marginBottom(12.px) } }) { TIcon("clipboard-list") }
            Div { Text(if (isAdmin) "Ei pohjia vielä — luo ensimmäinen." else "Ei pohjia saatavilla tällä hetkellä.") }
        }
        else -> Div(attrs = {
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fill, minmax(280px, 1fr))")
                gap(16.px)
            }
        }) {
            pohjat.forEach { pohja ->
                PohjaCard(pohja, isAdmin, onSelect = { onSelectPohja(pohja) }, onEdit = { onEditPohja(pohja) }, onDelete = { onDeletePohja(pohja) })
            }
        }
    }
}

@Composable
private fun PohjaCard(pohja: PerehdytysPohja, isAdmin: Boolean, onSelect: () -> Unit, onEdit: () -> Unit, onDelete: () -> Unit) {
    var isHovered by remember { mutableStateOf(false) }
    Div(attrs = {
        style {
            property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(20.px)
            property("border", "1px solid ${if (isHovered) "#2563EB" else "#E9ECEF"}")
            property("box-shadow", if (isHovered) KaihuTheme.ShadowMd else KaihuTheme.ShadowSm)
            property("transition", "all 0.2s ease")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px)
        }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexStart) } }) {
            Div(attrs = {
                style {
                    width(44.px); height(44.px); backgroundColor(KaihuTheme.PrimaryLight); borderRadius(12.px)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); fontSize(1.4.em)
                }
            }) { TIcon("clipboard-list") }
            val (bg, fg, lbl) = when (pohja.kohderyhma) {
                "huolto" -> Triple("#FFF3CD", "#856404", "Huolto+Admin")
                "admin"  -> Triple("#D8F3DC", "#1B4332", "Vain admin")
                else     -> Triple("#334155", "#CBD5E1", "Kaikki")
            }
            Span(attrs = { style { backgroundColor(Color(bg)); color(Color(fg)); padding(3.px, 10.px); borderRadius(20.px); fontSize(0.72.em); fontWeight("700") } }) { Text(lbl) }
        }
        Div {
            Div(attrs = { style { fontWeight("700"); color(KaihuTheme.TextPrimary); marginBottom(4.px) } }) { Text(pohja.otsikko) }
            if (pohja.kuvaus.isNotBlank()) Div(attrs = { style { fontSize(0.85.em); color(KaihuTheme.TextSecondary); lineHeight("1.4") } }) { Text(pohja.kuvaus) }
            Div(attrs = { style { fontSize(0.8.em); color(KaihuTheme.TextLight); marginTop(4.px) } }) {
                Text("${pohja.kysymykset.size} kysymystä")
            }
        }
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); gap(8.px); paddingTop(8.px)
                property("border-top", "1px solid ${KaihuTheme.Border}")
                flexWrap(FlexWrap.Wrap); alignItems(AlignItems.Center)
            }
        }) {
            SmallButton(text = "Täytä", icon = "pencil", onClick = { onSelect() })
            if (isAdmin) SmallButton(text = "Muokkaa", icon = "edit", onClick = { onEdit() }, backgroundColor = KaihuTheme.Secondary)
            if (isAdmin) SmallButton(text = "", icon = "trash", onClick = { onDelete() }, isDanger = true)
        }
    }
}

// =====================================================
// CREATE POHJA FORM
// =====================================================

private fun pohjaNewId(): String = js("(Date.now().toString(36) + Math.random().toString(36).substr(2, 9))") as String

@Composable
private fun CreatePohjaForm(
    user: User,
    editingPohja: PerehdytysPohja? = null,
    onSave: (PerehdytysPohja) -> Unit,
    onCancel: () -> Unit
) {
    val isMobile = window.innerWidth <= 900
    var otsikko by remember(editingPohja?.id) { mutableStateOf(editingPohja?.otsikko ?: "") }
    var kuvaus by remember(editingPohja?.id) { mutableStateOf(editingPohja?.kuvaus ?: "") }
    var kohderyhma by remember(editingPohja?.id) { mutableStateOf(editingPohja?.kohderyhma ?: "kaikki") }
    var kysymykset by remember(editingPohja?.id) { mutableStateOf(editingPohja?.kysymykset ?: emptyList()) }
    var lisaaKortit by remember(editingPohja?.id) { mutableStateOf(editingPohja?.kysymykset?.any { it.id.startsWith("kortti_") } ?: false) }
    var isSaving by remember { mutableStateOf(false) }

    val korttiKysymykset = listOf(
        PohjaKysymys(id = "kortti_heading", teksti = "Kortit", tyyppi = "heading"),
        PohjaKysymys(id = "kortti_tyoturvallisuus", teksti = "Työturvallisuus – Voimassa", tyyppi = "text"),
        PohjaKysymys(id = "kortti_tulityo", teksti = "Tulityö – Voimassa", tyyppi = "text"),
        PohjaKysymys(id = "kortti_tieturva1", teksti = "Tieturva 1 – Voimassa", tyyppi = "text"),
        PohjaKysymys(id = "kortti_ensiapu", teksti = "Ensiapu – Voimassa", tyyppi = "text"),
        PohjaKysymys(id = "kortti_vesityo", teksti = "Vesityökortti – Voimassa", tyyppi = "text"),
        PohjaKysymys(id = "kortti_tieturva2", teksti = "Tieturva 2 – Voimassa", tyyppi = "text"),
        PohjaKysymys(id = "kortti_lupa", teksti = "Kirjallinen lupa taakan kiinnittämiseen / alamiehenä toimimiseen", tyyppi = "checkbox"),
    )

    Div(attrs = { style { padding(20.px) } }) {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px); marginBottom(24.px) } }) {
            Div(attrs = { onClick { onCancel() }; style { color(KaihuTheme.Primary); cursor("pointer"); fontWeight("600"); fontSize(0.9.em) } }) { Text("← Takaisin") }
            H2(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary); fontWeight("800") } }) { Text(if (editingPohja != null) "Muokkaa pohjaa" else "Luo perehdytyspohja") }
        }

        Div(attrs = {
            style {
                display(if (isMobile) DisplayStyle.Flex else DisplayStyle.Grid)
                if (isMobile) flexDirection(FlexDirection.Column) else property("grid-template-columns", "1fr 1fr")
                gap(24.px); alignItems(AlignItems.Start)
            }
        }) {
            // Vasen: editori
            Div(attrs = {
                style {
                    property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(24.px)
                    property("border", "1px solid ${KaihuTheme.Border}")
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px)
                }
            }) {
                StyledInput(label = "Otsikko *", value = otsikko, onValueChange = { otsikko = it }, placeholder = "Esim. Yleinen perehdytys")
                StyledInput(label = "Kuvaus (vapaaehtoinen)", value = kuvaus, onValueChange = { kuvaus = it }, placeholder = "Lyhyt kuvaus pohjasta")

                Div {
                    Div(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary); marginBottom(10.px); fontSize(0.9.em) } }) { Text("Kohderyhmä") }
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
                        KOHDERYHMAT.forEach { (value, label) -> KohderyhmäChip(label, value, kohderyhma == value) { kohderyhma = value } }
                    }
                }

                Div(attrs = {
                    style {
                        padding(12.px); borderRadius(10.px)
                        property("border", "1px solid ${if (lisaaKortit) "#2563EB" else KaihuTheme.Border}")
                        backgroundColor(if (lisaaKortit) Color("#1E3A5F") else KaihuTheme.Background)
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px)
                        cursor("pointer"); property("transition", "all 0.15s")
                    }
                    onClick {
                        val uusiTila = !lisaaKortit
                        lisaaKortit = uusiTila
                        kysymykset = if (uusiTila) {
                            kysymykset.filter { !it.id.startsWith("kortti_") } + korttiKysymykset
                        } else {
                            kysymykset.filter { !it.id.startsWith("kortti_") }
                        }
                    }
                }) {
                    Div(attrs = {
                        style {
                            width(20.px); height(20.px); borderRadius(5.px); property("flex-shrink", "0")
                            property("border", "2px solid ${if (lisaaKortit) "#2563EB" else "#6B7280"}")
                            backgroundColor(if (lisaaKortit) KaihuTheme.Primary else Color.transparent)
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                            color(Color.white); fontWeight("700"); fontSize(0.85.em)
                        }
                    }) { if (lisaaKortit) TIcon("check") }
                    Div {
                        Div(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary); fontSize(0.9.em) } }) {
                            Text("Lisää kortit-osio lomakkeeseen")
                        }
                        Div(attrs = { style { fontSize(0.78.em); color(KaihuTheme.TextSecondary); marginTop(2.px) } }) {
                            Text("Työturvallisuus, Tulityö, Tieturva 1, Ensiapu, Vesityökortti, Tieturva 2, Kirjallinen lupa")
                        }
                    }
                }

                Div {
                    Div(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary); marginBottom(10.px); fontSize(0.9.em) } }) {
                        Text("Kysymykset (${kysymykset.size})")
                    }

                    if (kysymykset.isEmpty()) {
                        P(attrs = { style { color(KaihuTheme.TextLight); fontStyle("italic"); fontSize(0.88.em); margin(0.px, 0.px, 8.px, 0.px) } }) {
                            Text("Käytä alla olevia nappeja lisätäksesi kysymyksiä.")
                        }
                    }

                    kysymykset.forEachIndexed { i, k ->
                        PohjaKysymysEditor(
                            kysymys = k,
                            index = i,
                            total = kysymykset.size,
                            onChange = { updated -> kysymykset = kysymykset.toMutableList().also { it[i] = updated } },
                            onDelete = { kysymykset = kysymykset.toMutableList().also { it.removeAt(i) } },
                            onMoveUp = { if (i > 0) kysymykset = kysymykset.toMutableList().also { it.add(i - 1, it.removeAt(i)) } },
                            onMoveDown = { if (i < kysymykset.size - 1) kysymykset = kysymykset.toMutableList().also { it.add(i + 1, it.removeAt(i)) } }
                        )
                    }

                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap); marginTop(4.px) } }) {
                        SmallButton("+ Checkbox", onClick = {
                            kysymykset = kysymykset + PohjaKysymys(id = pohjaNewId(), teksti = "", tyyppi = "checkbox")
                        }, backgroundColor = KaihuTheme.Primary)
                        SmallButton("+ Monivalinta", onClick = {
                            kysymykset = kysymykset + PohjaKysymys(id = pohjaNewId(), teksti = "", tyyppi = "choice", vaihtoehdot = listOf("", ""))
                        }, backgroundColor = KaihuTheme.Secondary)
                        SmallButton("+ Väliotsikko", onClick = {
                            kysymykset = kysymykset + PohjaKysymys(id = pohjaNewId(), teksti = "", tyyppi = "heading")
                        }, backgroundColor = KaihuTheme.Grey)
                    }

                }

                Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); justifyContent(JustifyContent.FlexEnd); marginTop(8.px) } }) {
                    StyledButton("Peruuta", onClick = onCancel, backgroundColor = KaihuTheme.Grey)
                    StyledButton(
                        text = if (isSaving) "Tallennetaan..." else if (editingPohja != null) "Tallenna muutokset" else "Tallenna",
                        enabled = !isSaving,
                        backgroundColor = KaihuTheme.Primary,
                        onClick = {
                            if (otsikko.isBlank()) { com.kaihuoy.components.common.AppFeedback.showWarning("Anna pohjalle otsikko."); return@StyledButton }
                            isSaving = true
                            val pohja = PerehdytysPohja(
                                otsikko = otsikko.trim(),
                                kuvaus = kuvaus.trim(),
                                kohderyhma = kohderyhma,
                                lisatty = Date.now().toLong(),
                                lisaajaId = user.uid,
                                kysymykset = kysymykset.map { it.copy(teksti = it.teksti.trim()) }
                            )
                            onSave(pohja)
                        }
                    )
                }
            }

            // Oikea: live esikatselu
            Div(attrs = {
                style {
                    property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(24.px)
                    property("border", "1px solid ${KaihuTheme.Border}")
                    if (!isMobile) { property("position", "sticky"); top(20.px) }
                }
            }) {
                P(attrs = {
                    style {
                        fontWeight("600"); color(KaihuTheme.TextSecondary); margin(0.px, 0.px, 16.px, 0.px)
                        fontSize(0.8.em); property("text-transform", "uppercase"); property("letter-spacing", "0.06em")
                    }
                }) { Text("Esikatselu") }

                if (otsikko.isNotBlank()) {
                    H3(attrs = { style { margin(0.px, 0.px, 4.px, 0.px); color(KaihuTheme.TextPrimary) } }) { Text(otsikko) }
                }
                if (kuvaus.isNotBlank()) {
                    P(attrs = { style { color(KaihuTheme.TextSecondary); margin(0.px, 0.px, 16.px, 0.px); fontSize(0.9.em) } }) { Text(kuvaus) }
                }

                if (kysymykset.isEmpty()) {
                    P(attrs = { style { color(KaihuTheme.TextLight); fontStyle("italic") } }) {
                        Text("Lisää kysymyksiä niin näet esikatselun tässä.")
                    }
                } else {
                    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
                        kysymykset.forEach { q ->
                            when (q.tyyppi) {
                                "heading" -> Div(attrs = {
                                    style { marginTop(12.px); paddingBottom(6.px); property("border-bottom", "2px solid ${KaihuTheme.Border}") }
                                }) {
                                    Span(attrs = { style { fontWeight("700"); color(KaihuTheme.TextPrimary); fontSize(0.95.em) } }) {
                                        Text(q.teksti.ifBlank { "(Väliotsikko)" })
                                    }
                                }
                                "text" -> Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
                                    Span(attrs = { style { fontSize(0.88.em); color(KaihuTheme.TextSecondary); fontWeight("600") } }) {
                                        Text(q.teksti.ifBlank { "(Tekstikenttä)" })
                                    }
                                    Div(attrs = {
                                        style {
                                            width(140.px); height(28.px); borderRadius(6.px)
                                            property("border", "1px solid ${KaihuTheme.Border}")
                                            backgroundColor(KaihuTheme.Background)
                                        }
                                    })
                                }
                                "choice" -> Div {
                                    P(attrs = { style { fontWeight("600"); margin(0.px, 0.px, 6.px, 0.px); fontSize(0.9.em) } }) {
                                        Text(q.teksti.ifBlank { "(Monivalintakysymys)" })
                                    }
                                    q.vaihtoehdot.filter { it.isNotBlank() }.ifEmpty { listOf("(vaihtoehto)") }.forEach { opt ->
                                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); padding(3.px, 0.px) } }) {
                                            Div(attrs = { style { width(14.px); height(14.px); borderRadius(50.percent); property("border", "2px solid #CED4DA"); property("flex-shrink", "0") } })
                                            Span(attrs = { style { fontSize(0.88.em); color(KaihuTheme.TextSecondary) } }) { Text(opt) }
                                        }
                                    }
                                }
                                else -> Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                                    Div(attrs = { style { width(17.px); height(17.px); borderRadius(4.px); property("border", "2px solid #CED4DA"); property("flex-shrink", "0") } })
                                    Span(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextPrimary) } }) {
                                        Text(q.teksti.ifBlank { "(Checkbox-kysymys)" })
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PohjaKysymysEditor(
    kysymys: PohjaKysymys,
    index: Int,
    total: Int,
    onChange: (PohjaKysymys) -> Unit,
    onDelete: () -> Unit,
    onMoveUp: () -> Unit,
    onMoveDown: () -> Unit
) {
    val typeLabel = when (kysymys.tyyppi) {
        "heading" -> "Väliotsikko"
        "choice" -> "Monivalinta"
        "text" -> "Tekstikenttä"
        else -> "Checkbox"
    }
    val typeColor: CSSColorValue = when (kysymys.tyyppi) {
        "heading" -> KaihuTheme.Grey
        "choice" -> KaihuTheme.Secondary
        "text" -> Color("#7C3AED")
        else -> KaihuTheme.Primary
    }

    Div(attrs = {
        style {
            backgroundColor(KaihuTheme.Background); borderRadius(12.px); padding(12.px)
            property("border", "1px solid ${KaihuTheme.Border}"); marginBottom(8.px)
        }
    }) {
        Div(attrs = {
            style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween); marginBottom(8.px); gap(8.px); flexWrap(FlexWrap.Wrap) }
        }) {
            Div(attrs = { style { display(DisplayStyle.Flex); gap(4.px); flexWrap(FlexWrap.Wrap) } }) {
                listOf("checkbox" to "Checkbox", "text" to "Tekstikenttä", "choice" to "Monivalinta", "heading" to "Väliotsikko").forEach { (t, label) ->
                    val isActive = kysymys.tyyppi == t
                    Span(attrs = {
                        onClick {
                            if (!isActive) {
                                val updated = when (t) {
                                    "choice" -> kysymys.copy(tyyppi = t, vaihtoehdot = if (kysymys.vaihtoehdot.size >= 2) kysymys.vaihtoehdot else listOf("", ""))
                                    else -> kysymys.copy(tyyppi = t, vaihtoehdot = emptyList())
                                }
                                onChange(updated)
                            }
                        }
                        style {
                            backgroundColor(if (isActive) typeColor else KaihuTheme.Grey)
                            color(Color.white); padding(3.px, 10.px); borderRadius(20.px)
                            fontSize(0.78.em); fontWeight("700"); cursor("pointer")
                            opacity(if (isActive) 1.0 else 0.45)
                        }
                    }) { Text(label) }
                }
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(4.px) } }) {
                if (index > 0) SmallButton("↑", onClick = onMoveUp)
                if (index < total - 1) SmallButton("↓", onClick = onMoveDown)
                SmallButton("Poista", onClick = onDelete, isDanger = true)
            }
        }

        StyledInput(
            label = when (kysymys.tyyppi) { "heading" -> "Väliotsikon teksti"; "text" -> "Kentän nimi"; else -> "Kysymys / kohta" },
            value = kysymys.teksti,
            placeholder = when (kysymys.tyyppi) {
                "heading" -> "Esim. Turvallisuus"
                "text" -> "Esim. Työturvallisuus – Voimassa"
                "choice" -> "Esim. Millä kulkuneuvolla tulet töihin?"
                else -> "Esim. Paloturvallisuusohjeet käyty läpi"
            },
            onValueChange = { onChange(kysymys.copy(teksti = it)) }
        )

        if (kysymys.tyyppi == "choice") {
            Div(attrs = { style { marginTop(4.px) } }) {
                Label(attrs = { style { fontSize(0.85.em); fontWeight("600"); color(KaihuTheme.TextSecondary); display(DisplayStyle.Block); marginBottom(6.px) } }) {
                    Text("Vaihtoehdot")
                }
                kysymys.vaihtoehdot.forEachIndexed { i, opt ->
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); alignItems(AlignItems.Center); marginBottom(6.px) } }) {
                        Div(attrs = { style { width(12.px); height(12.px); borderRadius(50.percent); property("border", "2px solid #CED4DA"); property("flex-shrink", "0") } })
                        Input(type = InputType.Text, attrs = {
                            value(opt)
                            onInput { e ->
                                val newOpts = kysymys.vaihtoehdot.toMutableList().also { list -> list[i] = e.value }
                                onChange(kysymys.copy(vaihtoehdot = newOpts))
                            }
                            attr("placeholder", "Vaihtoehto ${i + 1}")
                            style {
                                flex(1); padding(6.px, 10.px); borderRadius(8.px)
                                border(1.px, LineStyle.Solid, KaihuTheme.Border); fontSize(0.9.em)
                                property("background", KaihuTheme.CardGradient); outline("none")
                            }
                        })
                        if (kysymys.vaihtoehdot.size > 1) {
                            SmallButton("×", onClick = {
                                val newOpts = kysymys.vaihtoehdot.toMutableList().also { it.removeAt(i) }
                                onChange(kysymys.copy(vaihtoehdot = newOpts))
                            }, isDanger = true)
                        }
                    }
                }
                SmallButton("+ Lisää vaihtoehto", onClick = {
                    onChange(kysymys.copy(vaihtoehdot = kysymys.vaihtoehdot + ""))
                })
            }
        }
    }
}

// =====================================================
// FILL POHJA VIEW
// =====================================================

@Composable
private fun FillPohjaView(pohja: PerehdytysPohja, user: User?, onBack: () -> Unit) {
    val scope = rememberCoroutineScope()
    val korttiTextIds = setOf("kortti_tyoturvallisuus", "kortti_tulityo", "kortti_tieturva1", "kortti_ensiapu", "kortti_vesityo", "kortti_tieturva2")
    var vastaukset by remember { mutableStateOf(pohja.kysymykset.associate { k ->
        k.id to when {
            k.id in korttiTextIds -> ""
            k.tyyppi == "text" -> ""
            else -> "false"
        }
    }) }
    var isSaving by remember { mutableStateOf(false) }
    var tyomaat by remember { mutableStateOf<List<Tyomaa>>(emptyList()) }
    var selectedTyomaa by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        try {
            FirebaseService.database.reference("tyomaat").valueEvents.collect { snapshot ->
                tyomaat = snapshot.children.mapNotNull { child ->
                    try { val t = child.value<Tyomaa>(); if (t.id.isBlank()) t.copy(id = child.key ?: "") else t }
                    catch (_: Exception) { null }
                }.sortedBy { it.nimi }
            }
        } catch (_: Exception) {}
    }

    Div(attrs = { style { padding(20.px); maxWidth(760.px); property("margin", "0 auto") } }) {
        Div(attrs = {
            onClick { onBack() }
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
                color(KaihuTheme.Primary); cursor("pointer"); fontWeight("600"); marginBottom(16.px); fontSize(0.9.em)
                property("width", "fit-content")
            }
        }) { Text("← Takaisin") }

        H2(attrs = { style { margin(0.px, 0.px, 6.px, 0.px); color(KaihuTheme.TextPrimary) } }) { Text(pohja.otsikko) }
        if (pohja.kuvaus.isNotBlank()) P(attrs = { style { margin(0.px, 0.px, 20.px, 0.px); color(KaihuTheme.TextSecondary) } }) { Text(pohja.kuvaus) }

        // Työmaavalinta
        if (tyomaat.isNotEmpty()) {
            Div(attrs = {
                style {
                    property("background", KaihuTheme.CardGradient); borderRadius(12.px); padding(16.px)
                    property("border", "1px solid ${KaihuTheme.Border}"); marginBottom(16.px)
                }
            }) {
                StyledDropdown(
                    value = selectedTyomaa,
                    options = listOf(DropdownOption("", "Valitse työmaa...")) + tyomaat.map { t ->
                        DropdownOption(t.nimi, if (t.osoite.isNotBlank()) "${t.nimi} (${t.osoite})" else t.nimi)
                    },
                    placeholder = "Valitse työmaa...",
                    label = "Työmaa",
                    onSelect = { selectedTyomaa = it }
                )
            }
        }

        if (pohja.kysymykset.isEmpty()) {
            Div(attrs = { style { padding(40.px); textAlign("center"); color(KaihuTheme.TextSecondary) } }) {
                Text("Tähän pohjaan ei ole lisätty kysymyksiä.")
            }
        } else {
            Div(attrs = {
                style {
                    property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(24.px)
                    property("border", "1px solid ${KaihuTheme.Border}"); marginBottom(20.px)
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px)
                }
            }) {
                var questionIndex = 0
                pohja.kysymykset.forEach { kysymys ->
                    val effectiveTyyppi = if (kysymys.id in korttiTextIds) "text" else kysymys.tyyppi
                    when (effectiveTyyppi) {
                        "heading" -> Div(attrs = {
                            style {
                                marginTop(8.px); paddingBottom(8.px)
                                property("border-bottom", "2px solid ${KaihuTheme.Border}")
                            }
                        }) {
                            Span(attrs = { style { fontWeight("700"); color(KaihuTheme.TextPrimary); fontSize(1.em) } }) {
                                Text(kysymys.teksti.ifBlank { "—" })
                            }
                        }
                        "text" -> {
                            questionIndex++
                            val raw = if (vastaukset[kysymys.id] == "false" || vastaukset[kysymys.id] == "true") "" else vastaukset[kysymys.id] ?: ""
                            val parts = raw.split("/")
                            val kkVal = parts.getOrElse(0) { "" }
                            val vvVal = parts.getOrElse(1) { "" }
                            Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
                                Span(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextSecondary); fontWeight("600") } }) {
                                    Text(kysymys.teksti)
                                }
                                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) {
                                    Input(type = InputType.Text, attrs = {
                                        value(kkVal)
                                        onInput { e ->
                                            val uusiVv = vastaukset[kysymys.id]?.split("/")?.getOrElse(1) { "" } ?: ""
                                            vastaukset = vastaukset.toMutableMap().also { it[kysymys.id] = "${e.value}/$uusiVv" }
                                        }
                                        attr("placeholder", "kk")
                                        attr("maxlength", "2")
                                        style {
                                            padding(8.px, 10.px); borderRadius(8.px); fontSize(0.9.em); width(52.px); textAlign("center")
                                            property("border", "1px solid ${KaihuTheme.Border}")
                                            property("background", KaihuTheme.CardGradient); outline("none"); color(KaihuTheme.TextPrimary)
                                        }
                                    })
                                    Span(attrs = { style { fontWeight("700"); color(KaihuTheme.TextSecondary); fontSize(1.1.em) } }) { Text("/") }
                                    Input(type = InputType.Text, attrs = {
                                        value(vvVal)
                                        onInput { e ->
                                            val uusiKk = vastaukset[kysymys.id]?.split("/")?.getOrElse(0) { "" } ?: ""
                                            vastaukset = vastaukset.toMutableMap().also { it[kysymys.id] = "$uusiKk/${e.value}" }
                                        }
                                        attr("placeholder", "vv")
                                        attr("maxlength", "2")
                                        style {
                                            padding(8.px, 10.px); borderRadius(8.px); fontSize(0.9.em); width(52.px); textAlign("center")
                                            property("border", "1px solid ${KaihuTheme.Border}")
                                            property("background", KaihuTheme.CardGradient); outline("none"); color(KaihuTheme.TextPrimary)
                                        }
                                    })
                                }
                            }
                        }
                        "choice" -> {
                            questionIndex++
                            val current = vastaukset[kysymys.id] ?: ""
                            Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) } }) {
                                Span(attrs = { style { fontSize(0.95.em); color(KaihuTheme.TextPrimary); fontWeight("600") } }) {
                                    Text("$questionIndex. ${kysymys.teksti}")
                                }
                                kysymys.vaihtoehdot.forEach { opt ->
                                    val isSelected = current == opt
                                    Div(attrs = {
                                        style {
                                            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px)
                                            padding(8.px, 12.px); borderRadius(8.px); cursor("pointer")
                                            backgroundColor(if (isSelected) Color("#1E3A5F") else Color.transparent)
                                            property("border", "1px solid ${if (isSelected) "#2563EB" else KaihuTheme.Border}")
                                            property("transition", "all 0.15s")
                                        }
                                        onClick { vastaukset = vastaukset.toMutableMap().also { it[kysymys.id] = opt } }
                                    }) {
                                        Div(attrs = {
                                            style {
                                                width(16.px); height(16.px); borderRadius(50.percent); property("flex-shrink", "0")
                                                property("border", "2px solid ${if (isSelected) "#2563EB" else "#6B7280"}")
                                                backgroundColor(if (isSelected) KaihuTheme.Primary else Color.transparent)
                                            }
                                        })
                                        Span(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextPrimary) } }) { Text(opt) }
                                    }
                                }
                            }
                        }
                        else -> {
                            questionIndex++
                            val checked = vastaukset[kysymys.id] == "true"
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px)
                                    padding(12.px); borderRadius(10.px)
                                    backgroundColor(if (checked) Color("#1E3A5F") else Color.transparent)
                                    property("border", "1px solid ${if (checked) "#2563EB" else KaihuTheme.Border}")
                                    property("transition", "all 0.15s"); cursor("pointer")
                                }
                                onClick { vastaukset = vastaukset.toMutableMap().also { it[kysymys.id] = if (checked) "false" else "true" } }
                            }) {
                                Div(attrs = {
                                    style {
                                        width(22.px); height(22.px); borderRadius(6.px); property("flex-shrink", "0")
                                        property("border", "2px solid ${if (checked) "#2563EB" else "#6B7280"}")
                                        backgroundColor(if (checked) KaihuTheme.Primary else Color.transparent)
                                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                                        color(Color.white); fontWeight("700"); fontSize(0.85.em)
                                    }
                                }) { if (checked) TIcon("check") }
                                Span(attrs = { style { fontSize(0.95.em); color(KaihuTheme.TextPrimary) } }) {
                                    Text("$questionIndex. ${kysymys.teksti}")
                                }
                            }
                        }
                    }
                }
            }

            Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(12.px) } }) {
                StyledButton("Peruuta", onClick = onBack, backgroundColor = KaihuTheme.Grey)
                StyledButton(
                    text = if (isSaving) "Tallennetaan..." else "Lähetä vastaukset",
                    enabled = !isSaving && user != null,
                    backgroundColor = KaihuTheme.Primary,
                    onClick = {
                        if (user == null) return@StyledButton
                        isSaving = true
                        scope.launch {
                            try {
                                val vastaus = PerehdytysVastaus(
                                    pohjaId = pohja.id,
                                    pohjaOtsikko = pohja.otsikko,
                                    userId = user.uid,
                                    userName = user.displayName,
                                    employer = user.employer,
                                    birthDate = user.birthDate,
                                    taxNumber = user.taxNumber,
                                    address = user.address,
                                    phone = user.phone,
                                    tyomaa = selectedTyomaa,
                                    aika = Date.now().toLong(),
                                    vastaukset = vastaukset
                                )
                                val key = FirebaseService.database.reference("perehdytys_vastaukset").push().key ?: return@launch
                                FirebaseService.database.reference("perehdytys_vastaukset").child(key).setValue(vastaus.copy(id = key))
                                com.kaihuoy.components.common.AppFeedback.showSuccess("Vastaukset tallennettu!")
                                onBack()
                            } catch (e: Exception) {
                                com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
                                isSaving = false
                            }
                        }
                    }
                )
            }
        }
    }
}

// =====================================================
// PIENET APUKOMPONENTIT
// =====================================================

@Composable
private fun SourceToggle(label: String, isActive: Boolean, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            padding(8.px, 16.px); borderRadius(8.px); cursor("pointer")
            fontWeight(if (isActive) "700" else "500"); fontSize(0.88.em)
            backgroundColor(if (isActive) KaihuTheme.Primary else KaihuTheme.Background)
            color(if (isActive) Color.white else KaihuTheme.TextSecondary)
            property("border", if (isActive) "2px solid #2563EB" else "2px solid transparent")
            property("transition", "all 0.15s"); property("user-select", "none")
        }
    }) { Text(label) }
}

@Composable
internal fun KohderyhmäChip(label: String, value: String, isSelected: Boolean, onClick: () -> Unit) {
    val (bg, fg, border) = when {
        !isSelected -> Triple("#334155", "#CBD5E1", "#475569")
        value == "admin"  -> Triple("#D8F3DC", "#1B4332", "#2563EB")
        value == "huolto" -> Triple("#FFF3CD", "#856404", "#F59E0B")
        else              -> Triple("#2563EB", "#FFFFFF", "#2563EB")
    }
    Div(attrs = {
        onClick { onClick() }
        style {
            padding(8.px, 16.px); borderRadius(20.px); cursor("pointer"); fontWeight("600"); fontSize(0.88.em)
            backgroundColor(Color(bg)); color(Color(fg))
            property("border", "2px solid $border")
            property("transition", "all 0.15s"); property("user-select", "none")
        }
    }) { Text(if (isSelected) "✓ $label" else label) }
}
